import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./AfiliadosDashboard.css";

export const AfiliadosDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [affiliateData, setAffiliateData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [commission, setCommission] = useState(0);

  const [monthlyStats, setMonthlyStats] = useState({ registrations: 0, commission: 0 });
  const [historicalStats, setHistoricalStats] = useState({ registrations: 0, commission: 0 });
  const [planSummary, setPlanSummary] = useState([]);

  const getDateRange = (start, end) => {
    const dates = [];
    const current = new Date(start);
  
    while (current <= new Date(end)) {
      dates.push(new Date(current).toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
  
    return dates;
  };

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay.toISOString().split("T")[0]);
    setEndDate(today.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (user && user.affiliateCoupon) {
        try {
          const response = await fetch(
            `https://us-central1-erastro-326a1.cloudfunctions.net/getAffiliateData?couponId=${user.affiliateCoupon}`
          );
          if (!response.ok) throw new Error("Error al obtener los datos de afiliados");
          const data = await response.json();
          console.log(data.data)
          setAffiliateData(data.data);
        } catch (error) {
          console.error("Error:", error);
        }
      }
    };

    if (user?.affiliate) {
      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (affiliateData) {
      filterAndProcessData();
      calculatePlanSummary();
    }
  }, [affiliateData, startDate, endDate]);

  const getLastDayOfCurrentMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 0);
  };

  const calculatePlanSummary = () => {
    if (!affiliateData?.couponUsage?.redemptionDates) return;
    
    const planMap = {};
    
    affiliateData.couponUsage.redemptionDates.forEach(redemption => {
      const plan = redemption.subscriptionType;
      planMap[plan] = (planMap[plan] || 0) + 1;
    });
    
    const summary = Object.entries(planMap).map(([plan, timesUsed]) => ({
      plan,
      timesUsed
    }));
    
    setPlanSummary(summary);
  };

  const filterAndProcessData = () => {
    if (!affiliateData?.couponUsage?.redemptionDates) return;
    
    const registrations = affiliateData.couponUsage.redemptionDates || [];

    const filtered = registrations.filter((r) => {
      const date = new Date(r.date).toISOString().split("T")[0];
      return date >= startDate && date <= endDate;
    });  

    // Agrupar por fecha con conteo
    const groupedData = {};
    filtered.forEach((r) => {
      const key = new Date(r.date).toISOString().split("T")[0];
      groupedData[key] = (groupedData[key] || 0) + 1;
    });

    // Rellenar los días faltantes con 0
    const allDates = getDateRange(startDate, endDate);
    const filledChartData = allDates.map((date) => ({
      name: date,
      registros: groupedData[date] || 0,
    }));

    setFilteredData(filledChartData);

    const total = filtered.length;
    setTotalRegistrations(total);
    setCommission(total * 1.5);

    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = getLastDayOfCurrentMonth();
    const currentMonthRegs = registrations.filter((r) => {
      const d = new Date(r.date);
      return d >= monthStart && d <= monthEnd;
    });

    setMonthlyStats({
      registrations: currentMonthRegs.length,
      commission: currentMonthRegs.length * 1.5,
    });

    setHistoricalStats({
      registrations: registrations.length,
      commission: registrations.length * 1.5,
    });
  };

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (!user.affiliate) navigate("/");
    }
  }, [user, loading, navigate]);

  if (loading || !user || !user.affiliate) {
    return null;
  }

  return (
    <div className="afiliados-dashboard">
      <h1>Dashboard de Afiliado</h1>
      <div className="dashboard-main">
        {/* Columna izquierda */}
        <div className="dashboard-left">
          <div className="dashboard-card user-card">
            <h2 className="card-title">Tu Información</h2>
            <div className="card-content">
              <div className="info-row">
                <span>Usuario:</span>
                <span>{user.username}</span>
              </div>
              <div className="info-row">
                <span>Correo:</span>
                <span>{user.email}</span>
              </div>
              <div className="info-row">
                <span>Código de afiliado:</span>
                <div className="coupon-container">
                  <span className="coupon-code">{affiliateData?.couponCode || "No disponible"}</span>
                  <button className="copy-btn" onClick={() => navigator.clipboard.writeText(affiliateData?.couponCode || "")}>
                    copiar
                  </button>
                </div>
              </div>
              <div className="info-row">
                <span>Proximo Pago:</span>
                <span>{new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toLocaleDateString("es-ES")}</span>
              </div>
            </div>
          </div>

          <div className="card finance-summary">
            <h2 className="card__title">Resumen Financiero</h2>

            <div className="finance-summary__section">
              <h3 className="section__title">Este mes</h3>
              <div className="section__stats">
                <div className="stat">
                  <span className="stat__label">Suscripciones</span>
                  <span className="stat__value">{monthlyStats.registrations}</span>
                </div>
                <div className="stat">
                  <span className="stat__label">Beneficios</span>
                  <span className="stat__value">€{monthlyStats.commission.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="finance-summary__section">
              <h3 className="section__title">Periodo seleccionado</h3>
              <div className="section__stats">
                <div className="stat">
                  <span className="stat__label">Suscripciones</span>
                  <span className="stat__value">{totalRegistrations}</span>
                </div>
                <div className="stat">
                  <span className="stat__label">Beneficios</span>
                  <span className="stat__value">€{commission.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="finance-summary__section">
              <h3 className="section__title">Total histórico</h3>
              <div className="section__stats">
                <div className="stat">
                  <span className="stat__label">Suscripciones</span>
                  <span className="stat__value">{historicalStats.registrations}</span>
                </div>
                <div className="stat">
                  <span className="stat__label">Beneficios</span>
                  <span className="stat__value">€{historicalStats.commission.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Columna derecha */}
        <div className="dashboard-right">
          {/* Filtro de fechas justo encima del gráfico */}
          <div className="dashboard-filters">
            <div className="input-group">
              <label>Desde</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="input-group">
              <label>Hasta</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <button className="period-btn" onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              setStartDate(firstDay.toISOString().split("T")[0]);
              setEndDate(today.toISOString().split("T")[0]);
            }}>
              Ver mes actual
            </button>
          </div>

          <div className="dashboard-card chart-card match-height">
            <div className="chart-header">
              <h2 className="card-title">Registros por día</h2>
              <div className="chart-stats">
                <span>{totalRegistrations} suscripciones</span>
                <span>€{commission.toFixed(2)} comisión</span>
              </div>
            </div>
            <div className="chart-container">
              {filteredData.length === 0 ? (
                <p className="no-data">No hay registros para el periodo seleccionado</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="registros" fill="#4f46e5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fila inferior: Distribución de Planes */}
      <div className="dashboard-card types-card">
        <h2 className="card-title">Distribución de Planes vendidos en total</h2>
        <div className="types-container">
          {planSummary.length === 0 ? (
            <p className="no-data">No hay datos disponibles</p>
          ) : (
            planSummary.map(({ plan, timesUsed }) => {
              const total = planSummary.reduce((sum, p) => sum + p.timesUsed, 0);
              const percentage = ((timesUsed / total) * 100).toFixed(1);
              const getColor = (plan) => {
                if (/mensual/i.test(plan)) return '#4f46e5';
                if (/trimestral/i.test(plan)) return '#7c3aed';
                if (/anual/i.test(plan)) return '#a855f7';
                return '#10b981';
              };
              return (
                <div key={plan} className="type-item">
                  <div className="type-name">{plan}</div>
                  <div className="progress-container">
                    <div className="progress-bar" style={{ width: `${percentage}%`, backgroundColor: getColor(plan) }}></div>
                  </div>
                  <div className="type-count">{timesUsed}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};